import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const { t, lang } = useLanguage();

  const services = [
    { label: t('serviceIndividual'), to: '/services#individual' },
    { label: t('serviceBusiness'), to: '/services#business' },
    { label: t('serviceSelfEmployed'), to: '/services#self-employed' },
    { label: t('serviceMultiState'), to: '/services#multi-state' },
    { label: t('servicePlanning'), to: '/services#planning' },
    { label: t('serviceAudit'), to: '/services#audit' },
  ];

  const quickLinks = [
    { label: t('navAbout'), to: '/about' },
    { label: t('navHowItWorks'), to: '/how-it-works' },
    { label: t('navWhoWeHelp'), to: '/who-we-help' },
    { label: t('bookAppt'), to: '/book-appointment' },
    { label: t('getQuote'), to: '/get-quote' },
    { label: t('navPortal'), to: '/portal' },
  ];

  return (
    <footer className="bg-[#0B1F3A] text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: '#D4AF37' }}>
                <Shield size={24} className="text-[#0B1F3A]" />
              </div>
              <div>
                <div className="font-heading font-bold text-lg leading-none">MaTaxPreparer</div>
                <div className="text-[10px] text-blue-300 tracking-widest uppercase">LLC</div>
              </div>
            </div>
            <p className="text-blue-200 text-sm leading-relaxed mb-5">
              {t('footerTagline')}
            </p>
            <p className="text-blue-300/70 text-xs mb-5">
              {t('footerNationwide')}
            </p>
            <div className="flex gap-3">
              {[Facebook, Twitter, Linkedin, Instagram].map((Icon, i) => (
                <a key={i} href="#" className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#D4AF37] flex items-center justify-center transition-colors duration-200">
                  <Icon size={16} className="text-white" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-semibold text-[#D4AF37] mb-5 text-sm uppercase tracking-widest">
              {t('navServices')}
            </h4>
            <ul className="space-y-3">
              {services.map((s) => (
                <li key={s.to}>
                  <Link to={s.to} className="text-blue-200 hover:text-white text-sm transition-colors duration-150">
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-[#D4AF37] mb-5 text-sm uppercase tracking-widest">
              {t('footerQuickLinks')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-blue-200 hover:text-white text-sm transition-colors duration-150">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-[#D4AF37] mb-5 text-sm uppercase tracking-widest">
              {t('footerContact')}
            </h4>
            <div className="space-y-4">
              <a href="tel:7702356999" className="flex items-start gap-3 text-blue-200 hover:text-white transition-colors duration-150 group">
                <Phone size={16} className="mt-0.5 flex-shrink-0 group-hover:text-[#D4AF37] transition-colors" />
                <span className="text-sm">770-235-6999</span>
              </a>
              <a href="mailto:mataxpreparerllc@yahoo.com" className="flex items-start gap-3 text-blue-200 hover:text-white transition-colors duration-150 group">
                <Mail size={16} className="mt-0.5 flex-shrink-0 group-hover:text-[#D4AF37] transition-colors" />
                <span className="text-sm break-all">mataxpreparerllc@yahoo.com</span>
              </a>
              <div className="flex items-start gap-3 text-blue-200">
                <MapPin size={16} className="mt-0.5 flex-shrink-0 text-[#D4AF37]" />
                <span className="text-sm">4384 Laughlin Ct NW<br />Kennesaw, GA 30144</span>
              </div>
            </div>
            <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-1">{t('businessHours')}</div>
              <div className="text-xs text-blue-200">{t('mondayFriday')}: 9:00 AM – 5:00 PM</div>
              <div className="text-xs text-blue-200">{t('saturday')}: 10:00 AM – 2:00 PM</div>
              <div className="text-xs text-blue-300/60">{t('sunday')}: {t('closed')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-blue-300 text-xs">
            © {new Date().getFullYear()} MaTaxPreparer LLC. {t('footerAllRights')}
          </p>
          <div className="flex items-center gap-6">
            {[
              { label: t('footerPrivacy'), to: '/privacy' },
              { label: t('footerTerms'), to: '/terms' },
              { label: t('footerCircular230'), to: '/circular-230' },
            ].map((l) => (
              <Link key={l.to} to={l.to} className="text-blue-300 hover:text-white text-xs transition-colors duration-150">
                {l.label}
              </Link>
            ))}
          </div>
          <div className="text-[10px] text-blue-400 text-center">
            {t('irsAuthorized')} | {t('governingLaw')}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
